import React from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  Leaf,
  Minus,
  Plus,
  ShoppingBag,
  Trash2,
  Utensils,
} from "lucide-react";

import {
  useCart,
} from "../../components/CartContext";

import "./css/CartScreen.css";


/* =========================================================
   IMAGE URL HELPER
========================================================= */

const getImageUrl = (image) => {

  if (!image) {
    return "";
  }


  const imageValue =
    typeof image === "string"
      ? image
      : image.url ||
        image.image ||
        "";


  if (!imageValue) {
    return "";
  }


  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://")
  ) {
    return imageValue;
  }


  return `${
    imageValue.startsWith("/")
      ? ""
      : "/"
  }${imageValue}`;

};


/* =========================================================
   COMPONENT
========================================================= */

function CartScreen() {

  const navigate =
    useNavigate();


  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     CART
  ===================================================== */

  const {
    cart,
    cartCount,
    cartTotal,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
  } = useCart();


  /* =====================================================
     HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     BACK TO MENU
  ===================================================== */

  const handleBackToMenu = () => {

    navigate(
      `${homePath}#theme9-menu`
    );

  };


  /* =====================================================
     CHECKOUT
  ===================================================== */

  const handleContinue = () => {

    if (
      !cart ||
      cart.length === 0
    ) {
      return;
    }


    navigate(
      `${homePath}/checkout`
    );

  };


  /* =====================================================
     FORMAT PRICE
  ===================================================== */

  const formatPrice = (
    price
  ) => {

    return Number(
      price || 0
    ).toFixed(2);

  };


  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (
    !cart ||
    cart.length === 0
  ) {

    return (

      <main className="theme9-cart-page">


        <section className="theme9-cart-empty">


          {/* ICON */}

          <div className="theme9-cart-empty-icon">

            <ShoppingBag
              size={35}
            />

          </div>


          {/* LABEL */}

          <span className="theme9-cart-label">

            YOUR ORDER

          </span>


          {/* TITLE */}

          <h1>
            Your cart is empty
          </h1>


          {/* DESCRIPTION */}

          <p>
            Your next fresh meal is
            only a few taps away.
            Explore our menu and add
            something delicious to
            your order.
          </p>


          {/* BUTTON */}

          <button
            type="button"
            className="
              theme9-cart-primary-button
            "
            onClick={
              handleBackToMenu
            }
          >

            Explore Our Menu

            <ArrowRight
              size={18}
            />

          </button>


          {/* DECORATION */}

          <div className="theme9-cart-empty-leaf">

            <Leaf
              size={24}
            />

          </div>


        </section>


      </main>

    );

  }


  /* =====================================================
     CART
  ===================================================== */

  return (

    <main className="theme9-cart-page">


      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <section className="theme9-cart-header">

        <div className="theme9-cart-container">


          {/* BACK BUTTON */}

          <button
            type="button"
            className="
              theme9-cart-back-button
            "
            onClick={
              handleBackToMenu
            }
          >

            <ArrowLeft
              size={17}
            />

            Back to Menu

          </button>


          {/* TITLE ROW */}

          <div className="theme9-cart-title-row">


            <div>

              <span className="theme9-cart-label">

                YOUR ORDER

              </span>


              <h1>
                Your Cart
              </h1>


              <p>
                Review your fresh
                favourites before
                continuing to checkout.
              </p>

            </div>


            {/* TABLE */}

            <div className="theme9-cart-table">

              <span>
                TABLE
              </span>

              <strong>
                {tableId}
              </strong>

            </div>


          </div>


        </div>

      </section>


      {/* =================================================
          CART CONTENT
      ================================================= */}

      <section className="theme9-cart-content">

        <div className="theme9-cart-container">


          <div className="theme9-cart-layout">


            {/* =============================================
                CART ITEMS
            ============================================== */}

            <div className="theme9-cart-items-section">


              {/* ITEMS HEADER */}

              <div className="theme9-cart-items-heading">


                <div className="theme9-cart-items-title">

                  <span className="theme9-cart-count">

                    {cartCount}

                  </span>


                  <div>

                    <span className="theme9-cart-small-label">

                      FRESH PICKS

                    </span>

                    <h2>
                      Items in your order
                    </h2>

                  </div>

                </div>


                <button
                  type="button"
                  className="
                    theme9-clear-cart
                  "
                  onClick={
                    clearCart
                  }
                >

                  <Trash2
                    size={14}
                  />

                  Clear all

                </button>


              </div>


              {/* ===========================================
                  ITEMS
              ============================================ */}

              <div className="theme9-cart-items">


                {cart.map(
                  (item) => {

                    const image =
                      getImageUrl(
                        item.image
                      );


                    return (

                      <article
                        className="
                          theme9-cart-item
                        "
                        key={
                          item.id
                        }
                      >


                        {/* IMAGE */}

                        <div className="
                          theme9-cart-item-image
                        ">

                          {image ? (

                            <img
                              src={
                                image
                              }
                              alt={
                                item.name
                              }
                            />

                          ) : (

                            <div className="
                              theme9-cart-image-placeholder
                            ">

                              <Utensils
                                size={25}
                              />

                            </div>

                          )}

                        </div>


                        {/* DETAILS */}

                        <div className="
                          theme9-cart-item-details
                        ">


                          <span className="
                            theme9-cart-item-category
                          ">

                            <Leaf
                              size={12}
                            />

                            {
                              item.categoryName ||
                              "FRESH FOOD"
                            }

                          </span>


                          <h3>
                            {item.name}
                          </h3>


                          {item.description && (

                            <p>
                              {
                                item.description
                              }
                            </p>

                          )}


                          <div className="
                            theme9-cart-item-price
                          ">

                            ₹
                            {
                              formatPrice(
                                item.price
                              )
                            }

                          </div>


                        </div>


                        {/* ACTIONS */}

                        <div className="
                          theme9-cart-item-actions
                        ">


                          {/* QUANTITY */}

                          <div className="
                            theme9-quantity-control
                          ">


                            <button
                              type="button"
                              onClick={() =>
                                decreaseQuantity(
                                  item.id
                                )
                              }
                              aria-label={
                                `Decrease ${item.name}`
                              }
                            >

                              <Minus
                                size={15}
                              />

                            </button>


                            <span>
                              {
                                item.quantity
                              }
                            </span>


                            <button
                              type="button"
                              onClick={() =>
                                increaseQuantity(
                                  item.id
                                )
                              }
                              aria-label={
                                `Increase ${item.name}`
                              }
                            >

                              <Plus
                                size={15}
                              />

                            </button>


                          </div>


                          {/* ITEM TOTAL */}

                          <strong className="
                            theme9-cart-item-total
                          ">

                            ₹
                            {
                              formatPrice(
                                Number(
                                  item.price
                                ) *
                                Number(
                                  item.quantity
                                )
                              )
                            }

                          </strong>


                          {/* REMOVE */}

                          <button
                            type="button"
                            className="
                              theme9-remove-item
                            "
                            onClick={() =>
                              removeFromCart(
                                item.id
                              )
                            }
                          >

                            <Trash2
                              size={13}
                            />

                            Remove

                          </button>


                        </div>


                      </article>

                    );

                  }
                )}


              </div>


            </div>


            {/* =============================================
                ORDER SUMMARY
            ============================================== */}

            <aside className="theme9-cart-summary">


              {/* SUMMARY ICON */}

              <div className="theme9-summary-icon">

                <ShoppingBag
                  size={21}
                />

              </div>


              <span className="theme9-cart-label">

                ORDER SUMMARY

              </span>


              <h2>
                Your order
              </h2>


              <p className="theme9-summary-description">

                Freshly prepared for
                table {tableId}.

              </p>


              {/* ===========================================
                  SUMMARY ITEMS
              ============================================ */}

              <div className="theme9-summary-items">


                {cart.map(
                  (item) => (

                    <div
                      className="
                        theme9-summary-item
                      "
                      key={
                        item.id
                      }
                    >


                      <span>

                        <small>
                          {
                            item.quantity
                          }×
                        </small>

                        {
                          item.name
                        }

                      </span>


                      <strong>

                        ₹
                        {
                          formatPrice(
                            Number(
                              item.price
                            ) *
                            Number(
                              item.quantity
                            )
                          )
                        }

                      </strong>


                    </div>

                  )
                )}


              </div>


              {/* DIVIDER */}

              <div className="
                theme9-summary-divider
              " />


              {/* SUBTOTAL */}

              <div className="
                theme9-summary-row
              ">

                <span>
                  Subtotal
                </span>

                <strong>

                  ₹
                  {
                    formatPrice(
                      cartTotal
                    )
                  }

                </strong>

              </div>


              {/* NOTE */}

              <p className="
                theme9-summary-note
              ">

                Taxes and service
                charges will be
                calculated at checkout.

              </p>


              {/* CHECKOUT */}

              <button
                type="button"
                className="
                  theme9-checkout-button
                "
                onClick={
                  handleContinue
                }
              >

                Continue to Checkout

                <ArrowRight
                  size={18}
                />

              </button>


              {/* ADD MORE */}

              <button
                type="button"
                className="
                  theme9-summary-menu-button
                "
                onClick={
                  handleBackToMenu
                }
              >

                <ArrowLeft
                  size={15}
                />

                Add More Items

              </button>


            </aside>


          </div>


        </div>

      </section>


      {/* =================================================
          BOTTOM NOTE
      ================================================= */}

      <section className="theme9-cart-note">

        <div className="theme9-cart-container">


          <span className="theme9-cart-note-icon">

            <Leaf
              size={17}
            />

          </span>


          <p>
            Fresh ingredients,
            delicious flavours and
            wholesome food prepared
            especially for your table.
          </p>


        </div>

      </section>


    </main>

  );

}


export default CartScreen;